import clsx from 'clsx';
import type { ReactNode } from 'react';

import { formatMinorAmount } from '@repo/common';
import { DataTestIds } from '@repo/frontend-shared';
import { SDSFlexbox } from '@repo/sds/SDSFlexbox';
import { SDSSeparator } from '@repo/sds/SDSSeparator';
import { SDSText } from '@repo/sds/SDSText';

import { describeCompletionRate, describeDaysRemaining, isBelowCompletionLimit } from './refund-format';

interface RefundMoney {
  amountMinor: number;
  currency: string;
}

export interface RefundEligibilityFactsProps {
  windowDays: number;
  daysRemaining: number;
  completionRatePercent: number;
  completionLimitPercent: number;
  amountLabel: string;
  amount: RefundMoney;
  refundToLabel?: string | null;
}

/**
 * Which purchase the dialog is about, above a rule — the first thing every
 * refund frame shows under its title.
 */
export function RefundPackageSummary({ productTitle, planLabel }: { productTitle: string; planLabel: string }) {
  return (
    <>
      <SDSFlexbox direction="column" alignItems="flex-start" dataTestId={DataTestIds.BILLING.REFUND.PACKAGE_SUMMARY}>
        <SDSText type="body-large" text={productTitle} />
        <SDSText type="body-large" text={planLabel} color="muted" />
      </SDSFlexbox>
      <SDSSeparator />
    </>
  );
}

/**
 * One label-and-value row, label on the left at a fixed width so the values
 * line up down the modal.
 */
export function RefundFact({
  label,
  children,
  dataTestId,
}: {
  label: string;
  children: ReactNode;
  dataTestId?: string;
}) {
  return (
    <SDSFlexbox direction="row" gap="input-regular" alignItems="center" fullWidth dataTestId={dataTestId}>
      <SDSFlexbox direction="row" alignItems="center" className="RefundFact__label">
        <SDSText type="body-medium" text={label} color="muted" />
      </SDSFlexbox>
      <SDSFlexbox direction="column" alignItems="flex-start" className="RefundFact__value">
        {children}
      </SDSFlexbox>
    </SDSFlexbox>
  );
}

/** Where a refund goes back to, with the frames' small reminder of why. */
export function RefundToValue({ refundToLabel }: { refundToLabel: string }) {
  return (
    <>
      <SDSText type="body-large" text={refundToLabel} color="text-secondary" />
      <SDSText type="button-medium" text="Original payment method" color="muted" />
    </>
  );
}

/**
 * The outlined pill beside the completion rate: brand green while there is
 * room under the cap, warning orange once it is reached. Outlined rather than
 * filled, as drawn — it qualifies the number beside it rather than shouting.
 */
function CompletionPill({ isBelowLimit }: { isBelowLimit: boolean }) {
  return (
    <SDSFlexbox
      className={clsx('RefundCompletionPill', { 'RefundCompletionPill--reached': !isBelowLimit })}
      alignItems="center"
      justifyContent="center"
      dataTestId={DataTestIds.BILLING.REFUND.COMPLETION_PILL}
    >
      <SDSText
        type="body-medium"
        text={isBelowLimit ? 'Below limit' : 'Limit reached'}
        color={isBelowLimit ? 'primary' : 'warning'}
      />
    </SDSFlexbox>
  );
}

/**
 * The figures every refund state shows — the window, the completion rate, the
 * money, and where it would go.
 *
 * Rendered whichever way the decision went: the design puts both rules on
 * screen even when only one of them did the refusing, so a member can see they
 * were inside one and outside the other.
 */
export function RefundEligibilityFacts({
  windowDays,
  daysRemaining,
  completionRatePercent,
  completionLimitPercent,
  amountLabel,
  amount,
  refundToLabel,
}: RefundEligibilityFactsProps) {
  return (
    <SDSFlexbox direction="column" gap="input-regular" fullWidth>
      <RefundRuleFacts
        windowDays={windowDays}
        daysRemaining={daysRemaining}
        completionRatePercent={completionRatePercent}
        completionLimitPercent={completionLimitPercent}
      />

      <RefundFact label={amountLabel} dataTestId={DataTestIds.BILLING.REFUND.AMOUNT_FACT}>
        <SDSText
          type="body-large"
          text={formatMinorAmount(amount.amountMinor, amount.currency)}
          color="text-secondary"
        />
      </RefundFact>

      {refundToLabel ? (
        <RefundFact label="Refund to" dataTestId={DataTestIds.BILLING.REFUND.REFUND_TO_FACT}>
          <RefundToValue refundToLabel={refundToLabel} />
        </RefundFact>
      ) : null}
    </SDSFlexbox>
  );
}

/**
 * The two refund rules as rows: the money-back window and the completion
 * rate against its limit. Shared by the request and details dialogs, which
 * draw them the same way.
 */
export function RefundRuleFacts({
  windowDays,
  daysRemaining,
  completionRatePercent,
  completionLimitPercent,
}: Pick<
  RefundEligibilityFactsProps,
  'windowDays' | 'daysRemaining' | 'completionRatePercent' | 'completionLimitPercent'
>) {
  return (
    <>
      <RefundFact label={`${windowDays}-day window`} dataTestId={DataTestIds.BILLING.REFUND.WINDOW_FACT}>
        <SDSText type="body-large" text={describeDaysRemaining(daysRemaining)} color="text-secondary" />
      </RefundFact>

      <RefundFact label="Completion rate" dataTestId={DataTestIds.BILLING.REFUND.COMPLETION_FACT}>
        {/* The pill sits at the row's far edge in every frame, not beside the
            figure, so the figure takes the slack. */}
        <SDSFlexbox direction="row" gap="input-regular" alignItems="center" fullWidth>
          <SDSText
            className="RefundFact__figure"
            type="body-large"
            text={describeCompletionRate(completionRatePercent)}
            color="text-secondary"
          />
          <CompletionPill isBelowLimit={isBelowCompletionLimit(completionRatePercent, completionLimitPercent)} />
        </SDSFlexbox>
      </RefundFact>
    </>
  );
}
